import { Icon, tokens } from '@hotjar/hotsauce';

import { between } from '@hotjar/insights-common/lib/between';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { WidgetContainer } from '@hotjar/webapp/features/SamplingWidget/Widget';
import { forwardRef } from 'react';
import { css } from '@emotion/react';
import { TrafficCoverageLevel } from '@hotjar/webapp/domains/revenue/sampling/types';

import { TrafficGaugeStatus } from './TrafficGaugeStatus';

interface Props {
  coveragePercentage: number;
  hasPartialMeasure: boolean;
  isCoverageMeasureComplete: boolean;
  coverageLevel: TrafficCoverageLevel;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  hideDropdownCaret?: boolean;
}

export const TrafficStatus = forwardRef<HTMLButtonElement, Props>(
  (
    {
      coveragePercentage,
      hasPartialMeasure,
      isCoverageMeasureComplete,
      coverageLevel,
      onClick,
      hideDropdownCaret,
    }: Props,
    ref,
  ): JSX.Element => {
    const { t } = useTranslationWithPrefix('common:sampling-widget');
    const showCheckIcon = isCoverageMeasureComplete && coveragePercentage === 100;
    const showVeryLowTrafficHighlight = coverageLevel === TrafficCoverageLevel.VeryLow ?? false;

    // TODO: refactor this to use `getTrafficCoverageLevelByPercentage` and `TrafficCoverageLevel`
    let text = '';
    if (!isCoverageMeasureComplete && !hasPartialMeasure) {
      text = t('traffic-coverage.long.active');
    } else if (between(coveragePercentage, 0, 25)) {
      text = t('traffic-coverage.long.very-low');
    } else if (between(coveragePercentage, 26, 50)) {
      text = t('traffic-coverage.long.low');
    } else if (between(coveragePercentage, 51, 75)) {
      text = t('traffic-coverage.long.medium');
    } else if (between(coveragePercentage, 76, 99)) {
      text = t('traffic-coverage.long.high');
    } else if (coveragePercentage > 99) {
      text = t('traffic-coverage.long.max');
    }

    return (
      <WidgetContainer
        // FIXME [DXFE]: Fix css prop incompatibility
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        css={
          showVeryLowTrafficHighlight &&
          css`
            background-color: ${tokens.color.bg_warning};
            border: 1px solid ${tokens.color.ui_border_warning};

            &:hover {
              background-color: ${tokens.color.bg_warning};
              border: 1px solid ${tokens.color.ui_border_alt};
            }
          `
        }
        ref={ref}
        iconComponent={
          showCheckIcon ? (
            <Icon size="m" icon="check_alt" color="text_success" />
          ) : (
            <TrafficGaugeStatus
              coveragePercentage={coveragePercentage}
              isCoverageMeasureComplete={hasPartialMeasure || isCoverageMeasureComplete}
              isHighlighted={showVeryLowTrafficHighlight}
            />
          )
        }
        onClick={onClick}
        text={text}
        hideDropdownCaret={hideDropdownCaret}
      />
    );
  },
);
